import { Component, OnDestroy, OnInit, ViewChild, HostListener } from "@angular/core";
import { formatMessage } from "devextreme/localization";
import notify from "devextreme/ui/notify";
import { DxDataGridComponent } from "devextreme-angular";
import { Subscription } from "rxjs";
import { PaperConfiguration, PaperConfigurationInput } from "../../shared/model/paper-configuration/paper-configuration";
import { PaperConfigurationService } from "../../shared/services/paper-configuration.service";
import { UserService } from "../../shared/services/user.service";
import { OrganizationService } from "../../shared/services/organization.service";
import { Publisher } from "../../shared/model/organization/organization";
import { User } from "../../shared/model/user/user";
import {
  ADMIN_US,
  KEY_USER_US,
} from "../../shared/model/roles";

@Component({
  selector: "app-paper-management",
  templateUrl: "./paper-management.component.html",
  styleUrls: ["./paper-management.component.scss"],
})
export class PaperManagementComponent implements OnInit, OnDestroy {
  @ViewChild(DxDataGridComponent) dataGrid: DxDataGridComponent;

  formatMessage = formatMessage;

  paperConfigurations: PaperConfiguration[] = [];
  publishers: Publisher[] = [];
  selectedPublisher: Publisher | null = null;
  user: User | null = null;
  isLoading = false;

  private subscriptions: Subscription[] = [];

  constructor(
    private paperConfigurationService: PaperConfigurationService,
    private userService: UserService,
    private organizationService: OrganizationService
  ) {}

  ngOnInit(): void {
    this.loadUserAndPublishers();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private loadUserAndPublishers(): void {
    this.isLoading = true;
    const userSub = this.userService.findOwnUser().subscribe({
      next: (user) => {
        this.user = user;

        // Admin and key users can see all publishers
        const isAdminUser = [ADMIN_US, KEY_USER_US].includes(user.role);

        if (isAdminUser) {
          this.organizationService.findAllPublisher().subscribe({
            next: (publishers) => {
              this.publishers = publishers.sort((a, b) =>
                a.displayName.localeCompare(b.displayName)
              );
              if (this.publishers.length === 1) {
                this.selectedPublisher = this.publishers[0];
                this.loadPaperConfigurations();
              }
              this.isLoading = false;
            },
            error: (err) => {
              console.error("Error loading publishers:", err);
              this.isLoading = false;
            },
          });
        } else {
          this.isLoading = false;
        }
      },
      error: (err) => {
        console.error("Error loading user:", err);
        this.isLoading = false;
      },
    });
    this.subscriptions.push(userSub);
  }

  onPublisherChanged(e: any): void {
    if (e.value) {
      this.selectedPublisher = e.value;
      this.loadPaperConfigurations();
    } else {
      this.selectedPublisher = null;
      this.paperConfigurations = [];
    }
  }

  private loadPaperConfigurations(): void {
    if (!this.selectedPublisher) {
      return;
    }
    this.isLoading = true;
    const sub = this.paperConfigurationService
      .findByPublisher(this.selectedPublisher.id)
      .subscribe({
        next: (configs) => {
          // Ensure enabled is always a boolean
          this.paperConfigurations = configs.map((config) => ({
            ...config,
            enabled: config.enabled !== undefined ? config.enabled : true,
          }));
          this.isLoading = false;
        },
        error: (err) => {
          console.error("Error loading paper configurations:", err);
          this.isLoading = false;
        },
      });
    this.subscriptions.push(sub);
  }

  addNewRow(): void {
    if (this.dataGrid && this.dataGrid.instance) {
      this.dataGrid.instance.addRow();
    }
  }

  onInitNewRow(e: any): void {
    // Set default values for new rows
    e.data.enabled = true;
    e.data.customerFurnished = true;
  }

  onRowInserting(e: any): void {
    if (!this.selectedPublisher) {
      e.cancel = true;
      return;
    }

    const newConfig = {
      publisherId: this.selectedPublisher.id,
      description: e.data.description || "",
      enabled: e.data.enabled !== undefined ? e.data.enabled : true,
      ppi: e.data.ppi,
      rollWidth: e.data.rollWidth,
      basisWeight: e.data.basisWeight,
      paperCoat: e.data.paperCoat,
      stock: e.data.stock,
      color: e.data.color,
      colorShort: e.data.colorShort,
      customerFurnished: e.data.customerFurnished !== undefined ? e.data.customerFurnished : true,
    };

    e.cancel = new Promise<void>((resolve, reject) => {
      this.paperConfigurationService.create(newConfig).subscribe({
        next: (created) => {
          e.data.id = created.id;
          e.data.publisherId = created.publisherId;
          resolve();
        },
        error: (err) => {
          console.error("Error creating paper configuration:", err);
          const errorMessage = this.extractErrorMessage(err);
          notify(errorMessage, "error", 5000);
          reject(err);
        },
      });
    });
  }

  onRowUpdating(e: any): void {
    // e.key is the id string directly (keyExpr="id")
    // e.oldData contains the full row data
    const updatedConfig = {
      id: e.key,
      publisherId: e.oldData.publisherId,
      description: e.newData.description ?? e.oldData.description,
      enabled:
        e.newData.enabled !== undefined
          ? e.newData.enabled
          : e.oldData.enabled ?? true,
      ppi: e.newData.ppi ?? e.oldData.ppi,
      rollWidth: e.newData.rollWidth ?? e.oldData.rollWidth,
      basisWeight: e.newData.basisWeight ?? e.oldData.basisWeight,
      paperCoat: e.newData.paperCoat ?? e.oldData.paperCoat,
      stock: e.newData.stock ?? e.oldData.stock,
      color: e.newData.color ?? e.oldData.color,
      colorShort: e.newData.colorShort ?? e.oldData.colorShort,
      customerFurnished:
        e.newData.customerFurnished !== undefined
          ? e.newData.customerFurnished
          : e.oldData.customerFurnished ?? true,
    };

    e.cancel = new Promise<void>((resolve, reject) => {
      this.paperConfigurationService.update(updatedConfig).subscribe({
        next: () => resolve(),
        error: (err) => {
          console.error("Error updating paper configuration:", err);
          const errorMessage = this.extractErrorMessage(err);
          notify(errorMessage, "error", 5000);
          reject(err);
        },
      });
    });
  }

  onRowRemoving(e: any): void {
    // e.key is the id string directly (keyExpr="id")
    e.cancel = new Promise<void>((resolve, reject) => {
      this.paperConfigurationService.delete(e.key).subscribe({
        next: () => resolve(),
        error: (err) => {
          console.error("Error deleting paper configuration:", err);
          reject(err);
        },
      });
    });
  }

  duplicateRow = (e: any): void => {
    if (!this.selectedPublisher) {
      return;
    }

    const originalData = e.row.data;
    const baseName = originalData.description;

    // Find a unique description by checking existing configurations
    const newDescription = this.ensureUniqueDescription(baseName);

    const newConfig = {
      publisherId: this.selectedPublisher.id,
      description: newDescription,
      ppi: originalData.ppi,
      rollWidth: originalData.rollWidth,
      basisWeight: originalData.basisWeight,
      paperCoat: originalData.paperCoat,
      stock: originalData.stock,
      color: originalData.color,
      colorShort: originalData.colorShort,
      customerFurnished: originalData.customerFurnished !== undefined ? originalData.customerFurnished : true,
      enabled: originalData.enabled !== undefined ? originalData.enabled : true,
    } as PaperConfigurationInput;

    this.paperConfigurationService.create(newConfig).subscribe({
      next: (created) => {
        // Reload configurations to show the new copy
        this.loadPaperConfigurations();
      },
      error: (err) => {
        console.error("Error duplicating paper configuration:", err);
        const errorMessage = this.extractErrorMessage(err);
        notify(errorMessage, "error", 5000);
      },
    });
  };

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    // Only handle paste if we're not currently editing a cell
    // (to avoid interfering with normal cell editing)
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return; // Let normal paste behavior work in inputs
    }

    if (!this.selectedPublisher) {
      return;
    }

    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain');
    if (text) {
      this.handlePasteData(text);
    }
  }

  private handlePasteData(text: string): void {
    if (!text || !this.selectedPublisher) {
      return;
    }

    // Split by newline to get rows
    const rows = text.split('\n').filter(row => row.trim());
    
    if (rows.length === 0) {
      return;
    }

    let addedCount = 0;
    let errorCount = 0;
    const descriptionsInBatch: string[] = [];

    rows.forEach((row) => {
      // Split by tab to get cells (Excel format)
      const cells = row.split('\t');
      
      if (cells.length < 3) {
        // Skip rows that don't have minimum required fields
        return;
      }

      let description = cells[0]?.trim() || '';
      
      // Make description unique if it already exists
      description = this.ensureUniqueDescription(description, descriptionsInBatch);
      descriptionsInBatch.push(description);

      const newConfig: PaperConfigurationInput = {
        publisherId: this.selectedPublisher!.id,
        description: description,
        ppi: cells[1] ? parseFloat(cells[1]) : undefined,
        rollWidth: cells[2] ? parseFloat(cells[2]) : 0,
        basisWeight: cells[3] ? parseFloat(cells[3]) : 0,
        paperCoat: cells[4]?.trim() || undefined,
        stock: cells[5]?.trim() || undefined,
        color: cells[6]?.trim() || undefined,
        colorShort: cells[7]?.trim() || undefined,
        customerFurnished: cells[8] ? cells[8].toLowerCase() === 'true' : true,
        enabled: cells[9] ? cells[9].toLowerCase() === 'true' : true,
      };

      // Validate required fields
      if (!newConfig.description || !newConfig.rollWidth || !newConfig.basisWeight) {
        errorCount++;
        return;
      }

      this.paperConfigurationService.create(newConfig).subscribe({
        next: () => {
          addedCount++;
          this.loadPaperConfigurations();
        },
        error: (err) => {
          errorCount++;
          console.error('Error creating paper configuration:', err);
        },
      });
    });

    // Show notification after processing
    setTimeout(() => {
      if (addedCount > 0) {
        notify(`Successfully added ${addedCount} paper configuration(s)`, 'success', 3000);
      }
      if (errorCount > 0) {
        notify(`Failed to add ${errorCount} row(s). Check required fields.`, 'error', 3000);
      }
    }, 500);
  }

  private ensureUniqueDescription(baseDescription: string, additionalDescriptions: string[] = []): string {
    // Check against existing configurations and current batch
    const allDescriptions = [
      ...this.paperConfigurations.map(config => config.description),
      ...additionalDescriptions
    ];

    if (!allDescriptions.includes(baseDescription)) {
      return baseDescription;
    }

    // Description exists, find a unique one
    let uniqueDescription = `${baseDescription} (Copy)`;
    let counter = 2;
    
    while (allDescriptions.includes(uniqueDescription)) {
      uniqueDescription = `${baseDescription} (Copy ${counter})`;
      counter++;
    }

    return uniqueDescription;
  }

  private extractErrorMessage(err: any): string {
    // Try to extract the message from GraphQL error response
    if (err?.graphQLErrors && err.graphQLErrors.length > 0) {
      return err.graphQLErrors[0].message;
    }
    if (err?.error?.errors && err.error.errors.length > 0) {
      return err.error.errors[0].message;
    }
    if (err?.message) {
      return err.message;
    }
    return "An error occurred while processing your request.";
  }
}
